import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Chip,
  Rating,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import MarketplaceService from "../../services/MarketplaceService";

const ProductManagement = () => {
  const { isSuperUser } = useAuth();
  const [products, setProducts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [currentProduct, setCurrentProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    imageUrl: "",
    category: "",
    rating: 4.0,
    stock: "",
    unit: "",
    weight: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);

  // Use MUI theme and responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Categories for products
  const categories = [
    "Fertilizer",
    "Tools",
    "Seeds",
    "Pesticides",
    "Equipment",
  ];
  const units = ["bag", "bottle", "pack", "set", "piece", "kg", "liter"];

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    setError("");
    try {
      const productsData = await MarketplaceService.getAllProducts();
      setProducts(productsData);
    } catch (error) {
      console.error("Error loading products:", error);
      setError("Failed to load products. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (type, product = null) => {
    if (!isSuperUser()) {
      setError("Only Super Users can manage marketplace products");
      return;
    }

    setDialogType(type);
    setCurrentProduct(product);

    if (product) {
      setFormData({
        name: product.name,
        description: product.description,
        price: product.price.toString(),
        imageUrl: product.imageUrl,
        category: product.category,
        rating: product.rating,
        stock: product.stock.toString(),
        unit: product.unit,
        weight: product.weight,
      });
    } else {
      setFormData({
        name: "",
        description: "",
        price: "",
        imageUrl: "",
        category: "Fertilizer",
        rating: 4.0,
        stock: "",
        unit: "bag",
        weight: "",
      });
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError("");
    setSuccess("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleRatingChange = (newValue) => {
    setFormData({
      ...formData,
      rating: newValue,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!isSuperUser()) {
      setError("Only Super Users can manage marketplace products");
      return;
    }

    // Validate form data
    if (
      !formData.name ||
      !formData.description ||
      !formData.price ||
      !formData.imageUrl ||
      !formData.category ||
      !formData.stock ||
      !formData.unit ||
      !formData.weight
    ) {
      setError("All fields are required");
      return;
    }

    // Validate price and stock are numbers
    if (isNaN(parseFloat(formData.price)) || parseFloat(formData.price) <= 0) {
      setError("Price must be a positive number");
      return;
    }

    if (isNaN(parseInt(formData.stock)) || parseInt(formData.stock) < 0) {
      setError("Stock must be a non-negative integer");
      return;
    }

    if (dialogType === "add") {
      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        imageUrl: formData.imageUrl,
        category: formData.category,
        rating: parseFloat(formData.rating),
        stock: parseInt(formData.stock),
        unit: formData.unit,
        weight: formData.weight,
        createdBy: 1, // You may want to get this from auth context
      };

      try {
        await MarketplaceService.createProduct(productData);
        setSuccess("Product added successfully");
        loadProducts(); // Reload products from API
      } catch (error) {
        setError(error.message || "Failed to create product");
        return;
      }
    } else if (dialogType === "edit") {
      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        imageUrl: formData.imageUrl,
        category: formData.category,
        rating: parseFloat(formData.rating),
        stock: parseInt(formData.stock),
        unit: formData.unit,
        weight: formData.weight,
      };

      try {
        await MarketplaceService.updateProduct(currentProduct.id, productData);
        setSuccess("Product updated successfully");
        loadProducts(); // Reload products from API
      } catch (error) {
        setError(error.message || "Failed to update product");
        return;
      }
    }

    // Close dialog after successful submission
    setTimeout(() => {
      handleCloseDialog();
    }, 1500);
  };

  const handleDelete = async (id) => {
    if (!isSuperUser()) {
      setError("Only Super Users can manage marketplace products");
      return;
    }

    try {
      await MarketplaceService.deleteProduct(id);
      setSuccess("Product deleted successfully");
      loadProducts(); // Reload products from API

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess("");
      }, 3000);
    } catch (error) {
      setError(error.message || "Failed to delete product");
    }
  };

  if (!isSuperUser()) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <LockIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          Restricted Access
        </Typography>
        <Typography variant="body1">
          Marketplace product management is restricted to Super Users only.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 1 : 0,
        }}
      >
        <Typography variant="h6" sx={{ mb: isMobile ? 1 : 0 }}>
          Marketplace Products
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog("add")}
          fullWidth={isMobile}
          size={isMobile ? "small" : "medium"}
        >
          Add Product
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2}>
        {products.map((product) => (
          <Grid item xs={12} sm={6} md={4} key={product.id}>
            <Card>
              <CardMedia
                component="img"
                height={isMobile ? "120" : "140"}
                image={product.imageUrl}
                alt={product.name}
              />
              <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography
                    variant={isMobile ? "subtitle1" : "h6"}
                    component="div"
                    gutterBottom
                    noWrap
                  >
                    {product.name}
                  </Typography>
                  <Chip
                    label={product.category}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Rating
                    value={product.rating}
                    precision={0.5}
                    readOnly
                    size="small"
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    ({product.rating})
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    mb: 1,
                  }}
                >
                  {product.description}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant={isMobile ? "subtitle1" : "h6"}
                    color="primary"
                  >
                    ${product.price}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Stock: {product.stock}
                  </Typography>
                </Box>
              </CardContent>
              <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
                <IconButton
                  color="primary"
                  onClick={() => handleOpenDialog("edit", product)}
                  size={isMobile ? "small" : "medium"}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() => handleDelete(product.id)}
                  size={isMobile ? "small" : "medium"}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Card>
          </Grid>
        ))}
        {products.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography>No products found</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {dialogType === "add" ? "Add New Product" : "Edit Product"}
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Product Name"
              type="text"
              fullWidth
              value={formData.name}
              onChange={handleChange}
              required
              size={isMobile ? "small" : "medium"}
            />

            <TextField
              margin="dense"
              name="description"
              label="Product Description"
              type="text"
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              value={formData.description}
              onChange={handleChange}
              required
              size={isMobile ? "small" : "medium"}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  name="price"
                  label="Price (RM)"
                  type="number"
                  fullWidth
                  value={formData.price}
                  onChange={handleChange}
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  name="stock"
                  label="Stock Quantity"
                  type="number"
                  fullWidth
                  value={formData.stock}
                  onChange={handleChange}
                  required
                  inputProps={{ min: 0 }}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  margin="dense"
                  size={isMobile ? "small" : "medium"}
                >
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  margin="dense"
                  size={isMobile ? "small" : "medium"}
                >
                  <InputLabel>Unit</InputLabel>
                  <Select
                    name="unit"
                    value={formData.unit}
                    onChange={handleChange}
                    required
                  >
                    {units.map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  name="weight"
                  label="Weight (with unit)"
                  type="text"
                  fullWidth
                  value={formData.weight}
                  onChange={handleChange}
                  required
                  helperText="e.g., 25kg, 1L, etc."
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ mt: 2 }}>
                  <Typography component="legend">Product Rating</Typography>
                  <Rating
                    name="rating"
                    value={parseFloat(formData.rating)}
                    precision={0.5}
                    onChange={(event, newValue) => {
                      handleRatingChange(newValue);
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            <TextField
              margin="dense"
              name="imageUrl"
              label="Image URL"
              type="url"
              fullWidth
              value={formData.imageUrl}
              onChange={handleChange}
              required
              helperText="Enter a valid image URL (e.g., https://example.com/image.jpg)"
              size={isMobile ? "small" : "medium"}
            />
          </DialogContent>

          <DialogActions>
            <Button
              onClick={handleCloseDialog}
              size={isMobile ? "small" : "medium"}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              size={isMobile ? "small" : "medium"}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ProductManagement;
